import { APIConfig } from "@/lib/api";
import { ApiError } from "@/lib/api/api-error";
import { request as __request } from "@/lib/api/request";
import {
  type UploadBulkConfirmPayload,
  type UploadBulkConfirmResponse,
  type UploadFileConfirmPayload,
  type UploadFileConfirmResponse,
  uploadBulkConfirmPayloadSchema,
  uploadBulkConfirmResponseSchema,
  uploadFileConfirmPayloadSchema,
  uploadFileConfirmResponseSchema,
  uploadRequestSchema,
} from "./types/uploads";

export class UploadService {
  static async createUploadRequest(files: File[]) {
    try {
      return await __request(
        APIConfig,
        {
          method: "POST",
          url: "/api/v1/orders/uploads/",
          formData: {
            files,
          },
        },
        uploadRequestSchema,
      );
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[createUploadRequest] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[createUploadRequest] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async getUploadRequest(id: string) {
    try {
      return await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/orders/uploads/{id}/",
          path: {
            id,
          },
        },
        uploadRequestSchema,
      );
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[getUploadRequest] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[getUploadRequest] Unexpected error:", error);
      }
      return null;
    }
  }

  static async confirmFile(
    uploadRequestId: string,
    fileId: string,
    payload?: UploadFileConfirmPayload,
  ): Promise<UploadFileConfirmResponse> {
    try {
      const body = uploadFileConfirmPayloadSchema.parse(payload ?? {});
      return await __request(
        APIConfig,
        {
          method: "POST",
          url: "/api/v1/orders/uploads/{id}/files/{fileId}/confirm/",
          path: {
            id: uploadRequestId,
            fileId,
          },
          mediaType: "application/json",
          body,
        },
        uploadFileConfirmResponseSchema,
      );
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[UploadService.confirmFile] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[UploadService.confirmFile] Unexpected error:", error);
      }
      throw error;
    }
  }

  static async confirmAll(
    uploadRequestId: string,
    payload?: UploadBulkConfirmPayload,
  ): Promise<UploadBulkConfirmResponse> {
    try {
      const body = uploadBulkConfirmPayloadSchema.parse(payload ?? {});
      return await __request(
        APIConfig,
        {
          method: "POST",
          url: "/api/v1/orders/uploads/{id}/confirm/",
          path: {
            id: uploadRequestId,
          },
          mediaType: "application/json",
          body,
        },
        uploadBulkConfirmResponseSchema,
      );
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[UploadService.confirmAll] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[UploadService.confirmAll] Unexpected error:", error);
      }
      throw error;
    }
  }
}
